<!--
SPDX-License-Identifier: LicenseRef-Proprietary
Наш файл (мартовская библиотека оформления, написана нами с нуля). См. LICENSING.md в корне репозитория.
-->
<script lang="ts">
	import { getLocalization } from '$lib/i18n';
	const { t } = getLocalization();
    import { browser } from '$app/environment';
    import { createTippy } from 'svelte-tippy';

    const tippy = createTippy({ arrow: true, animation: 'perspective-subtle', placement: 'bottom' });

    interface Theme {
        id: string;
        name: string;
        emoji: string;
        color: string;
    }

    const BASE_THEMES: Theme[] = [
        { id: 'default',     name: 'ClassQuiz',  emoji: '🟢', color: '#009444' },
        { id: 'dark',        name: 'Dark',        emoji: '🌲', color: '#4e6e58' },
        { id: 'ocean',       name: 'Ocean',       emoji: '🔵', color: '#0ea5e9' },
        { id: 'sunset',      name: 'Sunset',      emoji: '🟠', color: '#f97316' },
        { id: 'purple',      name: 'Purple',      emoji: '🟣', color: '#9333ea' },
    ];

    const SEASONAL_THEMES: Theme[] = [
        { id: 'winter',  name: 'Winter',  emoji: '❄️',  color: '#60a5fa' },
        { id: 'spring',  name: 'Spring',  emoji: '🌸',  color: '#ec4899' },
        { id: 'summer',  name: 'Summer',  emoji: '☀️',  color: '#eab308' },
        { id: 'autumn',  name: 'Autumn',  emoji: '🍂',  color: '#ea580c' },
    ];

    const HOLIDAY_THEMES: Theme[] = [
        { id: 'halloween',   name: 'Halloween',   emoji: '🎃', color: '#f97316' },
        { id: 'christmas',   name: 'Christmas',   emoji: '🎄', color: '#dc2626' },
        { id: 'newyear',     name: 'New Year',    emoji: '🎆', color: '#f59e0b' },
        { id: 'valentine',   name: 'Valentine',   emoji: '💝', color: '#e11d48' },
        { id: 'celebration', name: 'Celebration', emoji: '🎉', color: '#8b5cf6' },
    ];

    let currentTheme = $state('default');
    let isOpen = $state(false);

    if (browser) {
        currentTheme = localStorage.getItem('theme') ?? 'default';
    }

    const applyTheme = (themeId: string, manual = true) => {
        currentTheme = themeId;
        if (browser) {
            document.documentElement.dataset.theme = themeId;
            if (themeId === 'dark') {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
            localStorage.setItem('theme', themeId);
            if (manual) {
                localStorage.setItem('themeManuallySet', 'true');
            }
        }
        isOpen = false;
    };

    const toggleOpen = () => { isOpen = !isOpen; };

    const handleOutsideClick = (e: MouseEvent) => {
        if (!(e.target as Element).closest('.theme-switcher')) {
            isOpen = false;
        }
    };
</script>

<svelte:window onclick={handleOutsideClick} />

<div class="theme-switcher relative">
    <button
        onclick={toggleOpen}
        use:tippy={{ content: $t('words.switch_theme') }}
        aria-label={$t('words.switch_theme')}
        class="w-6 h-6 rounded-full border-2 border-gray-400 cursor-pointer transition-transform hover:scale-110"
        style="background-color: {[...BASE_THEMES, ...SEASONAL_THEMES, ...HOLIDAY_THEMES].find(t => t.id === currentTheme)?.color ?? '#009444'}"
    ></button>

    {#if isOpen}
        <div class="absolute right-0 top-8 w-56 rounded-lg shadow-xl z-50 p-3 text-sm"
             style="background-color: var(--theme-surface, rgba(255,255,255,0.95)); backdrop-filter: blur(12px); border: 1px solid rgba(0,0,0,0.1)">

            <p class="text-xs font-semibold uppercase tracking-wider mb-1" style="color: var(--theme-text); opacity: 0.6">Base</p>
            {#each BASE_THEMES as theme}
                <button
                    onclick={() => applyTheme(theme.id)}
                    class="w-full flex items-center gap-2 px-2 py-1 rounded hover:opacity-80 transition-opacity {currentTheme === theme.id ? 'font-bold' : ''}"
                    style="color: var(--theme-text)"
                >
                    <span class="w-4 h-4 rounded-full flex-shrink-0" style="background-color: {theme.color}"></span>
                    {theme.emoji} {theme.name}
                    {#if currentTheme === theme.id}<span class="ml-auto">✓</span>{/if}
                </button>
            {/each}

            <hr class="my-2 opacity-20" />

            <p class="text-xs font-semibold uppercase tracking-wider mb-1" style="color: var(--theme-text); opacity: 0.6">{$t('messages.theme_seasonal')}</p>
            {#each SEASONAL_THEMES as theme}
                <button
                    onclick={() => applyTheme(theme.id)}
                    class="w-full flex items-center gap-2 px-2 py-1 rounded hover:opacity-80 transition-opacity {currentTheme === theme.id ? 'font-bold' : ''}"
                    style="color: var(--theme-text)"
                >
                    <span class="w-4 h-4 rounded-full flex-shrink-0" style="background-color: {theme.color}"></span>
                    {theme.emoji} {theme.name}
                    {#if currentTheme === theme.id}<span class="ml-auto">✓</span>{/if}
                </button>
            {/each}

            <hr class="my-2 opacity-20" />

            <p class="text-xs font-semibold uppercase tracking-wider mb-1" style="color: var(--theme-text); opacity: 0.6">{$t('messages.theme_holidays')}</p>
            {#each HOLIDAY_THEMES as theme}
                <button
                    onclick={() => applyTheme(theme.id)}
                    class="w-full flex items-center gap-2 px-2 py-1 rounded hover:opacity-80 transition-opacity {currentTheme === theme.id ? 'font-bold' : ''}"
                    style="color: var(--theme-text)"
                >
                    <span class="w-4 h-4 rounded-full flex-shrink-0" style="background-color: {theme.color}"></span>
                    {theme.emoji} {theme.name}
                    {#if currentTheme === theme.id}<span class="ml-auto">✓</span>{/if}
                </button>
            {/each}
        </div>
    {/if}
</div>
